import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  listConformanceRuns,
  readReleaseConformance,
  startFullConformance,
} from "@/shared/api";
import type {
  ConformanceHistoryResponse,
  ConformanceRunResponse,
  ConformanceSummaryResponse,
} from "@/shared/api";
import { statusOf } from "@/shared/http/errors";

const SUMMARY_KEY = ["release-conformance"] as const;
const HISTORY_KEY = ["release-conformance", "history"] as const;
const ACTIVE = new Set(["QUEUED", "RUNNING"]);

export type ConformanceState =
  | { readonly status: "loading" }
  | { readonly status: "forbidden" }
  | { readonly status: "unavailable" }
  | { readonly status: "unreadable" }
  | {
      readonly status: "ready";
      readonly summary: ConformanceSummaryResponse;
      readonly history: ConformanceHistoryResponse;
      readonly startFull: () => void;
      readonly starting: boolean;
      readonly startFailed: boolean;
    };

export function useConformance(): ConformanceState {
  const queryClient = useQueryClient();
  const summary = useQuery({
    queryKey: SUMMARY_KEY,
    queryFn: async (): Promise<ConformanceSummaryResponse> => {
      const { data, error, response } = await readReleaseConformance();
      if (error !== undefined || data === undefined) {
        throw Object.assign(new Error("release conformance unreadable"), {
          status: response?.status,
        });
      }
      return data;
    },
    refetchInterval: (query) =>
      ACTIVE.has(query.state.data?.latest?.state ?? "") ? 2_000 : 30_000,
    retry: false,
  });
  const history = useQuery({
    queryKey: HISTORY_KEY,
    queryFn: async (): Promise<ConformanceHistoryResponse> => {
      const { data, error, response } = await listConformanceRuns({
        query: { limit: 20 },
      });
      if (error !== undefined) {
        throw Object.assign(new Error("conformance history unreadable"), {
          status: response?.status,
        });
      }
      return data;
    },
    refetchInterval: 30_000,
    retry: false,
  });
  const start = useMutation({
    mutationFn: async (): Promise<ConformanceRunResponse> => {
      const { data, error, response } = await startFullConformance();
      if (error !== undefined || data === undefined) {
        throw Object.assign(new Error("full conformance could not be queued"), {
          status: response?.status,
        });
      }
      return data;
    },
    onSuccess: (run) => {
      queryClient.setQueryData<ConformanceSummaryResponse>(SUMMARY_KEY, (current) =>
        current === undefined ? current : { ...current, latest: run },
      );
      queryClient.setQueryData<ConformanceHistoryResponse>(HISTORY_KEY, (current) => ({
        runs: [run, ...(current?.runs.filter((item) => item.run_id !== run.run_id) ?? [])],
      }));
    },
  });

  if (summary.isPending || history.isPending) return { status: "loading" };
  if (summary.isError || history.isError) {
    const status = statusOf(summary.error ?? history.error);
    if (status === 401 || status === 403) return { status: "forbidden" };
    if (status === 503) return { status: "unavailable" };
    return { status: "unreadable" };
  }
  return {
    status: "ready",
    summary: summary.data,
    history: history.data,
    startFull: start.mutate,
    starting: start.isPending,
    startFailed: start.isError,
  };
}
